import { defineStore } from 'pinia';

import { GatewayApiClient } from '@evidentid/gateway-api-client';
import { ProductUpdate } from '@evidentid/tprm-portal-lib/models/product-updates';
import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

export interface ProductUpdatesRequirement {
    gatewayClient: GatewayApiClient;
}

export interface ProductUpdatesState {
    productUpdates: ProductUpdate[];
    loadStatus: OperationStatus;
    archiveProductUpdates: ProductUpdate[];
    archiveLoadStatus: OperationStatus;
    dismissStatus: Record<string, OperationStatus>;
    dismissAllStatus: OperationStatus;
    archiveOpen: boolean;
}

function appOf(store: { $app: any }): ProductUpdatesRequirement {
    return store.$app;
}

const state = (): ProductUpdatesState => ({
    productUpdates: [],
    loadStatus: OperationStatus.uninitialized,
    archiveProductUpdates: [],
    archiveLoadStatus: OperationStatus.uninitialized,
    dismissStatus: {},
    dismissAllStatus: OperationStatus.uninitialized,
    archiveOpen: false,
});

export const useProductUpdatesStore = defineStore('productUpdates', {
    state,
    actions: {
        startLoadingProductUpdates() {
            this.loadStatus = OperationStatus.loading;
        },
        finishLoadingProductUpdates(productUpdates: ProductUpdate[]) {
            this.productUpdates = productUpdates;
            this.loadStatus = OperationStatus.success;
        },
        failLoadingProductUpdates() {
            this.loadStatus = OperationStatus.error;
        },
        startLoadingProductUpdatesArchive() {
            this.archiveLoadStatus = OperationStatus.loading;
        },
        finishLoadingProductUpdatesArchive(productUpdates: ProductUpdate[]) {
            this.archiveProductUpdates = productUpdates;
            this.archiveLoadStatus = OperationStatus.success;
        },
        failLoadingProductUpdatesArchive() {
            this.archiveLoadStatus = OperationStatus.error;
        },
        startDismissingProductUpdate(id: string) {
            this.dismissStatus = {
                ...this.dismissStatus,
                [id]: OperationStatus.loading,
            };
        },
        finishDismissingProductUpdate(id: string) {
            const markDismissed = (productUpdate: ProductUpdate) => {
                if (productUpdate.id !== id) {
                    return productUpdate;
                }

                return { ...productUpdate, dismissed: true };
            };
            this.productUpdates = this.productUpdates.map(markDismissed);
            this.archiveProductUpdates = this.archiveProductUpdates.map(markDismissed);
            this.dismissStatus = {
                ...this.dismissStatus,
                [id]: OperationStatus.success,
            };
        },
        failDismissingProductUpdate(id: string) {
            this.dismissStatus = {
                ...this.dismissStatus,
                [id]: OperationStatus.error,
            };
        },
        startDismissingAllProductUpdates() {
            this.dismissAllStatus = OperationStatus.loading;
        },
        finishDismissingAllProductUpdates() {
            const dismissedIds = new Set(this.productUpdates.map(({ id }) => id));
            const markDismissed = (productUpdate: ProductUpdate) => ({
                ...productUpdate,
                dismissed: true,
            });
            this.productUpdates = this.productUpdates.map(markDismissed);
            this.archiveProductUpdates = this.archiveProductUpdates.map((productUpdate) => {
                if (dismissedIds.has(productUpdate.id)) {
                    return markDismissed(productUpdate);
                }
                return productUpdate;
            });
            this.dismissAllStatus = OperationStatus.success;
        },
        failDismissingAllProductUpdates() {
            this.dismissAllStatus = OperationStatus.error;
        },
        openProductUpdatesArchive() {
            this.archiveOpen = true;
        },
        closeProductUpdatesArchive() {
            this.archiveOpen = false;
        },
        resetProductUpdates() {
            this.productUpdates = [];
            this.loadStatus = OperationStatus.uninitialized;
            this.archiveProductUpdates = [];
            this.archiveLoadStatus = OperationStatus.uninitialized;
            this.dismissStatus = {};
            this.dismissAllStatus = OperationStatus.uninitialized;
            this.archiveOpen = false;
        },
        async loadProductUpdates(payload: { force?: boolean } = {}) {
            if (!payload.force && [ OperationStatus.loading, OperationStatus.success ].includes(this.loadStatus)) {
                return true;
            }
            try {
                this.startLoadingProductUpdates();
                const productUpdates = await appOf(this).gatewayClient.tprmUi().getProductUpdates();
                this.finishLoadingProductUpdates(productUpdates);
                return true;
            } catch (error) {
                console.warn('load product updates error', error);
                this.failLoadingProductUpdates();
                return false;
            }
        },
        async loadProductUpdatesArchive(payload: { force?: boolean } = {}) {
            const isArchiveLoading = [
                OperationStatus.loading,
                OperationStatus.success,
            ].includes(this.archiveLoadStatus);
            if (!payload.force && isArchiveLoading) {
                return true;
            }
            try {
                this.startLoadingProductUpdatesArchive();
                const productUpdates = await appOf(this).gatewayClient.tprmUi().getProductUpdatesArchive();
                this.finishLoadingProductUpdatesArchive(productUpdates);
                return true;
            } catch (error) {
                console.warn('load product updates archive error', error);
                this.failLoadingProductUpdatesArchive();
                return false;
            }
        },
        async dismissProductUpdate(payload: { id: string }) {
            const { id } = payload;
            if (this.dismissStatus[id] === OperationStatus.loading) {
                return false;
            }
            try {
                this.startDismissingProductUpdate(id);
                await appOf(this).gatewayClient.tprmUi().dismissProductUpdate(id);
                this.finishDismissingProductUpdate(id);
                return true;
            } catch (error) {
                console.warn('dismiss product update error', error);
                this.failDismissingProductUpdate(id);
                return false;
            }
        },
        async dismissAllProductUpdates() {
            if (this.dismissAllStatus === OperationStatus.loading) {
                return false;
            }
            try {
                this.startDismissingAllProductUpdates();
                await appOf(this).gatewayClient.tprmUi().dismissAllProductUpdates();
                this.finishDismissingAllProductUpdates();
                return true;
            } catch (error) {
                console.warn('dismiss all product updates error', error);
                this.failDismissingAllProductUpdates();
                return false;
            }
        },
    },
});
